import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { IdUtils, UaService } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { EcmMaterialModule } from '@ecm/material';
import { ContractsBusinessModel, PortfolioService } from '@ecm/portfolio';
import { RouterService, TitleService } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { Dashboard } from '../../../../business/api/dashboard/dashboard';
import { Family } from '../../../../business/api/family/family';
import { CrossroadsViewModelMapper } from '../../../../business/mapper/crossroads/crossroads.view-model-mapper';
import { CrossroadsViewModel } from '../../../../business/model/crossroads/crossroads.view-model';
import { KenobiCodes } from '../../../../business/model/tracking/kenobiCodes';
import { DashboardService } from '../../../../services/dashboard.service';
import { FamilyService } from '../../../../services/family.service';
import { TrackingService } from '../../../../services/tracking.service';
import { AbstractParentComponent } from '../../../shared/abstract-parent.component';
import { CrossroadsUtils } from '../../utils/crossroads.utils';
import { ActiveBillComponent } from '../active-bill/active-bill.component';
import { MultiContractBillComponent } from '../multi-contract-bill/multi-contract-bill.component';
import { ProtectedHouseBillComponent } from '../protected-house-bill/protected-house-bill.component';
import { TerminatedContractBillComponent } from '../terminated-contract-bill/terminated-contract-bill.component';

@Component({
    selector: 'ecm-bills',
    templateUrl: 'bills.html',
    styleUrls: ['bills.css'],
    standalone: true,
    imports: [
        ActiveBillComponent,
        TerminatedContractBillComponent,
        ProtectedHouseBillComponent,
        MultiContractBillComponent,
        EcmMaterialModule,
        TranslateModule,
    ],
})
export class BillsComponent extends AbstractParentComponent implements OnInit {
    /**
     * Transaction properties for BCO monitoring
     */
    private readonly pageId: string;

    public billsViewModel: CrossroadsViewModel;

    public constructor(
        protected uaService: UaService,
        protected routerService: RouterService,
        protected route: ActivatedRoute,
        private readonly trackingService: TrackingService,
        private readonly dashboardService: DashboardService,
        private readonly familyService: FamilyService,
        private readonly titleService: TitleService,
        private readonly portfolioService: PortfolioService,
        private readonly translate: TranslateService,
    ) {
        super(uaService, routerService, route);
        translate.use('fr');
        this.pageId = IdUtils.generateRandomId();
    }

    public ngOnInit(): void {
        super.ngOnInit();
        EcmDispatcherModule.sendSurveyParams({ section: 'NEC', idPage: 'gt-home-page' });
        this.translate.get('dashboard').subscribe((billsTranslations: any) => {
            this.titleService.setTitle(billsTranslations.bills['title']);
            EcmDispatcherModule.sendBackRoute('accueil');
            this.dashboardService.getDashboard(KenobiCodes.FACTMC, this.pageId).subscribe((dashboard: Dashboard) => {
                this.portfolioService.getRawContracts().subscribe((contracts) => {
                    this.familyService
                        .isFamilyEligible(KenobiCodes.FACTMC, this.pageId)
                        .subscribe((isFamilyEligible: boolean) => {
                            if (isFamilyEligible) {
                                this.familyService
                                    .getFamily(KenobiCodes.CTROPT, this.pageId)
                                    .subscribe((family: Family) => {
                                        this.getCrossroads(
                                            dashboard,
                                            contracts,
                                            billsTranslations.contractsAndOptions,
                                            isFamilyEligible,
                                            family,
                                        );
                                    });
                            } else {
                                this.getCrossroads(
                                    dashboard,
                                    contracts,
                                    billsTranslations.contractsAndOptions,
                                    isFamilyEligible,
                                );
                            }
                        });
                });
            });
        });
    }

    /**
     * Check if sosh and call get crossRoadsViewModel
     * @param {Dashboard} dashboard
     * @param {ContractsBusinessModel} contracts
     * @param {any} translations
     * @param {boolean} isFamilyEligible
     * @param {Family} family (optional)
     */
    private getCrossroads(
        dashboard: Dashboard,
        contracts: ContractsBusinessModel,
        translations: any,
        isFamilyEligible: boolean,
        family?: Family,
    ) {
        this.isSoshBrand().subscribe((isSosh: boolean) => {
            CrossroadsUtils.orderModel(
                (this.billsViewModel = CrossroadsViewModelMapper.getCrossroadsViewModel(
                    dashboard,
                    contracts,
                    translations,
                    isFamilyEligible,
                    family,
                )),
                isSosh,
            );
            this.trackView();
        });
    }

    /**
     * Tracks view in kenobi and TMS
     */
    private trackView(): void {
        this.trackingService.trackBillsView();
        this.trackingService.trackKenobi(
            new KenobiParams(KenobiCodes.FACTMC['functionality'], KenobiCodes.FACTMC['rubric'], Status.OK),
            this.pageId,
        );
    }
}
